// Campaign domain models — brand-owned briefs that creators apply to.

/**
 * Every legal campaign status, as a runtime list so it can be checked against
 * the database rather than only asserted in a comment.
 *
 * Security-relevant since PIT-75: the `campaigns` select policy keys on this
 * column, and the `campaigns_status_known` check constraint pins the same
 * values. A status added here without a migration is one the policy has no
 * branch for — `campaign.statusParity.test.ts` fails if the two drift.
 */
export const CAMPAIGN_STATUSES = ['Active', 'In Progress', 'Paused'] as const;

/** Lifecycle status of a campaign. */
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

/**
 * The status at which a campaign is open to applications and visible to
 * creators. Paused hides it; 'In Progress' means work is underway with
 * creators already selected, so it stops inviting new ones.
 */
export const OPEN_CAMPAIGN_STATUS: CampaignStatus = 'Active';

/**
 * What the brand wants out of the campaign, what they want made, and how they
 * want it made (PIT-83).
 *
 * Runtime lists for the same reason CAMPAIGN_STATUSES is one: each is pinned by
 * a check constraint in 20260812183000_campaign_brief_fields.sql, and
 * `campaign.briefParity.test.ts` fails if a value is added here without the
 * migration to match. They are also the single source for the form's option
 * grids, so the buttons and the database cannot disagree.
 *
 * Unlike status, nothing keys on these — they are descriptive, not part of any
 * policy. See the migration for when the constraints would be worth dropping.
 */
export const CAMPAIGN_GOALS = ['Awareness', 'Sales', 'Content'] as const;

export const CAMPAIGN_DELIVERABLES = ['TikTok Video', 'UGC Ad', 'Product Photos', 'Reel'] as const;

export const CAMPAIGN_CONTENT_STYLES = [
  'Testimonial',
  'Unboxing',
  'Tutorial',
  'Storytelling',
  'Skit / Comedy',
  'Before & After',
  'Cinematic',
  'Voiceover',
] as const;

export type CampaignGoal = (typeof CAMPAIGN_GOALS)[number];
export type CampaignDeliverable = (typeof CAMPAIGN_DELIVERABLES)[number];
export type CampaignContentStyle = (typeof CAMPAIGN_CONTENT_STYLES)[number];

/**
 * How a brief field reads when the campaign does not carry one.
 *
 * Every campaign created before PIT-83 is in this state, and none of them can
 * be given an answer after the fact — the brand's choice was discarded, not
 * recorded elsewhere.
 */
export const UNSPECIFIED_BRIEF_FIELD = 'Not specified';

/**
 * A campaign brief PDF as shaped for rendering on a campaign card.
 * `path` is the Supabase Storage object path; `url` is a short-lived signed
 * download link minted at load time. Both are absent in demo mode.
 */
export interface CampaignBrief {
  /** Display filename, e.g. "brief.pdf". */
  name: string;
  /** Short-lived signed download URL, or null when unavailable. */
  url?: string | null;
  /** Storage object path (`<owner>/<campaign>/<filename>`). */
  path?: string;
}

/** App-facing campaign, as consumed by the campaign view and demo data. */
export interface Campaign {
  /** Numeric id for persisted rows; `local-*` string for unsaved local ones. */
  id: number | string;
  title: string;
  /** Optional product name the campaign promotes; null when cleared/unset in the DB. */
  product?: string | null;
  /** Formatted budget label, e.g. "$1,200". */
  budget: string;
  status: CampaignStatus;
  /** Number of creators who applied. */
  applied: number;
  /** Number of creators selected. */
  selected: number;
  /**
   * Turnaround label, e.g. "3–5 Days" or "ASAP" — not a date, despite the name
   * (PIT-83). Bundled demo rows carry date-like strings, which is where the
   * old "Apr 25, 2026" claim on this field came from. The migration explains
   * why it stays a label for now.
   */
  deadline: string;
  /** What the brand wants out of it; null on campaigns created before PIT-83. */
  goal?: CampaignGoal | null;
  /** What they want made; null on campaigns created before PIT-83. */
  deliverable?: CampaignDeliverable | null;
  /** How they want it made; null on campaigns created before PIT-83. */
  content_style?: CampaignContentStyle | null;
  /** Shaped brief for rendering; null when explicitly removed. */
  brief?: CampaignBrief | null;
}

/** Raw campaign row as stored in / returned from the `campaigns` table. */
export interface CampaignRow {
  id: number;
  owner_id: string | null;
  title: string | null;
  product: string | null;
  budget: string | null;
  status: string;
  applied: number;
  selected: number;
  /** Turnaround label ("3–5 Days"), not a date — see Campaign.deadline. */
  deadline: string | null;
  /** Null on every campaign created before PIT-83; the choice was discarded. */
  goal: string | null;
  deliverable: string | null;
  content_style: string | null;
  /** Storage path of the uploaded brief PDF. */
  brief_path: string | null;
  /** Original filename of the uploaded brief PDF. */
  brief_name: string | null;
  created_at?: string;
}
